import { AlertCircle, CheckCircle2, FileWarning } from "lucide-react";

import type { Artifact } from "@/api/artifacts";

/**
 * The `scientific_status` of one artifact, as a small inline badge.
 *
 * Lifted verbatim out of `ArtifactInputPicker` (flowstack#949) so the artifacts
 * list can show the field it now lets you filter on without growing a second
 * badge — the fork that #949 explicitly forbids, and the one that already bit
 * this codebase once: `components/runs/RunsTable.tsx` carried a placeholder
 * `StatusBadge` from before `RunStateBadge` landed, and it survived unimported
 * for long enough to be deleted rather than reconciled (VISUAL_SYSTEM U1.3).
 *
 * The unknown-string case is NEW rather than lifted. `GET /api/artifacts`
 * forwards `scientific_status` without validating it against the enum — the
 * route's own docstring says so, and names the GUI badge as the place to treat
 * an unknown value defensively. The picker's copy fell through to "no status",
 * which reports a status-carrying artifact as carrying none; here an
 * out-of-enum value renders in the flagged style with its raw text, so it reads
 * as "something is off with this value" rather than as absence.
 */
export function ArtifactStatusBadge({
  status,
}: {
  status: Artifact["scientific_status"] | string | null;
}): JSX.Element {
  if (status === "accepted") {
    return (
      <span className="flex items-center gap-1 text-state-completed-fg">
        <CheckCircle2 className="h-3 w-3" aria-hidden />
        accepted
      </span>
    );
  }
  if (status === "flagged") {
    return (
      <span className="flex items-center gap-1 text-state-failed-scientific-fg">
        <FileWarning className="h-3 w-3" aria-hidden />
        flagged
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span className="flex items-center gap-1 text-state-failed-validation-fg">
        <AlertCircle className="h-3 w-3" aria-hidden />
        failed
      </span>
    );
  }
  if (typeof status === "string" && status !== "") {
    return (
      <span className="flex items-center gap-1 text-state-failed-scientific-fg">
        <FileWarning className="h-3 w-3" aria-hidden />
        {status}
      </span>
    );
  }
  return <span className="text-muted-foreground">no status</span>;
}
